import React, { createContext, useState, useContext, useEffect } from 'react';
import { getApiUrl } from '../config/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');

        if (storedUser && storedToken) {
          const userData = JSON.parse(storedUser);

          // Check if token is expired
          if (userData.expiresAt && new Date(userData.expiresAt) > new Date()) {
            setUser({
              ...userData,
              token: storedToken
            });
          } else {
            // Token expired, clear storage
            localStorage.removeItem('user');
            localStorage.removeItem('token');
          }
        }
      } catch (err) {
        console.error('Error initializing auth:', err);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const register = async (name, email, password) => {
    try {
      setError(null);
      const response = await fetch(getApiUrl('/api/auth/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, password }),
      });

      // Handle non-JSON error responses
      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // Server returned HTML or other non-JSON error
        if (!response.ok) {
          throw new Error('Registration failed. Please try again.');
        }
        data = {};
      }

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      // Store user data
      const userData = {
        userId: data.user.userId,
        email: data.user.email,
        expiresAt: data.user.expiresAt
      };

      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', data.user.token);

      setUser({
        ...userData,
        token: data.user.token
      });

      return { success: true, message: data.message };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await fetch(getApiUrl('/api/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      // Handle non-JSON error responses
      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // Server returned HTML or other non-JSON error
        if (!response.ok) {
          throw new Error('Invalid email or password');
        }
        data = {};
      }

      if (!response.ok) {
        throw new Error(data.message || 'Invalid email or password');
      }

      // Store user data
      const userData = {
        userId: data.userId,
        email: data.email,
        expiresAt: data.expiresAt
      };

      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', data.token);

      setUser({
        ...userData,
        token: data.token
      });

      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const logout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
    setError(null);
  };

  const getAuthHeader = () => {
    if (user && user.token) {
      return { 'Authorization': `Bearer ${user.token}` };
    }
    return {};
  };

  const value = {
    user,
    loading,
    error,
    register,
    login,
    logout,
    getAuthHeader,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
